import { useState } from "react";
import { useAppAuth } from "../MockAuthProvider";
import { currentConfig } from "../config";
import { useAnalytics } from "./useAnalytics";
import { useStagedLoadingMessage } from "./useStagedLoadingMessage";

export const useCoachAI = () => {
  const auth = useAppAuth();
  const { trackMessageSent, trackFeedbackRequested } = useAnalytics();
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const loadingMessage = useStagedLoadingMessage(loading, streaming);

  const sendMessage = async ({
    messages,
    setMessages,
    userInput,
    setUserInput,
    promptContext = "",
    agentType = "roleplay",
  }) => {
    if (!userInput.trim()) return;

    if (!auth || !auth.isAuthenticated) {
      console.error("User is not authenticated");
      return;
    }

    // For OIDC auth, we need to include the token for API authentication
    const token = auth.user?.id_token;
    if (!token) {
      console.error("Missing authentication token");
      return;
    }

    setLoading(true);
    const newMessages = [...messages, { role: "user", content: userInput }];
    setMessages(newMessages);
    setUserInput("");

    // Record analytics
    trackMessageSent(agentType, userInput.length);

    // Build conversation history with context
    const conversationHistory = messages
      .map(msg => `${msg.role === "user" ? "Manager" : "Associate"}: ${msg.content}`)
      .join("\n");
    
    const prompt = promptContext 
      ? `${promptContext}\n\nConversation History:\n${conversationHistory}\n\nManager: ${userInput}`
      : `Conversation History:\n${conversationHistory}\n\nManager: ${userInput}`;

    try {
      const apiUrl = currentConfig.apiUrl;
      const response = await fetch(`${apiUrl}/coachai`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt,
          agentType,
          stream: true, // Enable streaming
        }),
      });

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      const role = agentType === "roleplay" ? "associate" : "assistant";
      
      if (data && data.text) {
        setMessages(prev => [...prev, { role, content: data.text }]);
      } else {
        setMessages(prev => [
          ...prev, 
          { 
            role: agentType === "roleplay" ? "associate" : "assistant", 
            content: "No response received."
          }
        ]);
      }
    } catch (error) {
      console.error("Error calling AI:", error);
      if (error.message.includes("401")) {
        console.error("Authentication error - please sign in again");
      }
      setMessages((prev) => [
        ...prev,
        {
          role: agentType === "roleplay" ? "associate" : "assistant",
          content: "Error retrieving AI response.",
        },
      ]);
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  const getFeedback = async ({
    messages,
    setMessages,
    selectedScenario,
    selectedMood,
  }) => {
    if (messages.length === 0) return;

    if (!auth || !auth.isAuthenticated) {
      console.error("User is not authenticated");
      return;
    }

    // For OIDC auth, we need to include the token for API authentication
    const token = auth.user?.id_token;
    if (!token) {
      console.error("Missing authentication token");
      return;
    }

    setLoading(true);

    // Record analytics
    trackFeedbackRequested(selectedScenario, selectedMood, messages.length);

    const formattedChat = messages
      .map(
        (msg) => `${msg.role === "user" ? "User" : "CoachAI"}: ${msg.content}`
      )
      .join("\n");

    const feedbackPrompt = `
      Analyze this ${selectedScenario} coaching conversation. The associate's mood was ${
      selectedMood || "standard"
    }.
  
      As a coaching expert, analyze this conversation between a manager and an associate. Provide concise feedback on:
      1. Strengths: Effective techniques and communication.
      2. Improvements: Missed opportunities and alternative approaches.
      3. Key Takeaways: Critical coaching moments and skills.
      4. Recommendations: Actionable tips for future coaching.
  
      Conversation:
      ${formattedChat}
    `;

    try {
      const apiUrl = currentConfig.apiUrl;
      const response = await fetch(`${apiUrl}/coachai`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: feedbackPrompt,
          agentType: "analysis",
        }),
      });

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      
      if (data && data.text) {
        // Add the complete feedback immediately
        setMessages(prev => [...prev, { role: "assistant", content: data.text }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: "No feedback received." }]);
      }
    } catch (error) {
      console.error("Error retrieving feedback:", error);
      if (error.message.includes("401")) {
        console.error("Authentication error - please sign in again");
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Error generating feedback. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  return {
    loading,
    streaming,
    loadingMessage,
    sendMessage,
    getFeedback,
  };
};
